/**
 * Recover from a deploy that replaced the hashed JS/CSS chunks.
 *
 * Every build wipes `dist/`, so a tab that booted on the previous
 * `index.html` fails as soon as it lazy-loads a route whose chunk no longer
 * exists ("Failed to fetch dynamically imported module", Vite's
 * `vite:preloadError`). The only fix is to load the new `index.html`, so we
 * reload once — throttled through sessionStorage so a genuinely broken
 * deploy cannot put the tab into a reload loop.
 */

const STORAGE_KEY = 'contup:stale-chunk-reload'
const MIN_INTERVAL_MS = 30_000

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i, // Safari
  /error loading dynamically imported module/i, // Firefox
  /Unable to preload CSS/i
]

export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '')
  return CHUNK_ERROR_PATTERNS.some(re => re.test(message))
}

/**
 * Do a full page load (`navigate`) unless we already did one in the last 30s.
 * Returns whether the navigation was issued.
 */
export function reloadForStaleChunk(
  now: () => number = Date.now,
  navigate: () => void = () => window.location.reload()
): boolean {
  let last = 0
  try {
    last = Number(sessionStorage.getItem(STORAGE_KEY) || 0)
  } catch {
    /* storage unavailable — still reload once per page life */
  }
  if (now() - last < MIN_INTERVAL_MS) return false
  try {
    sessionStorage.setItem(STORAGE_KEY, String(now()))
  } catch {
    /* ignore */
  }
  navigate()
  return true
}

interface RouterLike {
  onError: (handler: (error: unknown, to: { fullPath: string }) => void) => void
}

/**
 * Wire the `vite:preloadError` event and a router error hook.
 *
 * A lazy ROUTE chunk failing surfaces in `router.onError` with the target
 * route, so we full-load that URL and the user lands where they clicked.
 * `vite:preloadError` fires first for the same failure (and alone for
 * non-route dynamic imports); it is NOT default-prevented, so the error still
 * reaches the router, and its own reload is deferred a tick so the router
 * hook can take over when it applies.
 */
export function installStaleChunkReload(
  router: RouterLike,
  navigate: (url?: string) => void = url => (url ? window.location.assign(url) : window.location.reload()),
  deferMs = 150,
  target: EventTarget = window
): void {
  let fallback: ReturnType<typeof setTimeout> | undefined
  target.addEventListener('vite:preloadError', () => {
    if (fallback !== undefined) clearTimeout(fallback)
    fallback = setTimeout(() => {
      fallback = undefined
      reloadForStaleChunk(Date.now, () => navigate())
    }, deferMs)
  })
  router.onError((error, to) => {
    if (!isStaleChunkError(error)) return
    if (fallback !== undefined) {
      clearTimeout(fallback)
      fallback = undefined
    }
    reloadForStaleChunk(Date.now, () => navigate(to.fullPath))
  })
}
